<template>
  <div>
    <div class="all-dates-btn-container">
      <button
        class="all-dates-btn ys-text-button3"
        data-test="all-dates-btn"
        @click="toggleDatePicker"
      >
        <x-icon icon="filter-2" height="16" class="icon-list" />
        All Dates
      </button>
    </div>
    <control-recent-imagery-date-picker
      :visible="showDatePicker"
      :date-ranges="allDateRanges"
      :selected-range="selectedRange"
      @close="showDatePicker = false"
      @select="onDateRangeSelect"
    />
    <div class="recent-imagery-control">
      <div class="grid-container">
        <x-icon
          :class="['icon-hover', { 'icon-disabled': previousDisabled }]"
          data-test="recent-imagery-previous"
          icon="chevron-left"
          height="13"
          :color="previousDisabled ? '#A5A5A5' : ''"
          @click="previousDate"
        />
        <x-loading-spinner v-if="isLoading" />
        <ys-text
          v-else
          type="subtitle3"
          class="grid-text-center"
          data-test="recent-imagery-value"
        >
          {{ datesComputed }}
        </ys-text>
        <x-icon
          :class="['icon-hover', { 'icon-disabled': nextDisabled }]"
          data-test="recent-imagery-next"
          icon="chevron-right"
          height="13"
          :color="nextDisabled ? '#A5A5A5' : ''"
          @click="nextDate"
        />
      </div>
    </div>
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script setup>
import '@onxmaps/yellowstone-ui/ys-text'
import dateFormat from 'dateformat'
import { computed, onMounted, ref } from 'vue'
import xIcon from '@/components/shared/x-icon'
import xLoadingSpinner from '@/components/shared/x-loading-spinner'
import ControlRecentImageryDatePicker from '@/components/map-controls/control-recent-imagery-date-picker.vue'
import {
  canSetPrevious,
  canSetNext,
  fetchRecentImageryInfo,
  getImageryInfoFetched,
  getIsLoading,
  getStartDate,
  getEndDate,
  setPrevious,
  setNext,
  getIndex,
  getAllImageryDateRanges,
  setIndex,
} from '@/services/recent-imagery-service'
import marketingEventService from '@/services/marketing-event-service'

const previousDisabled = computed(() => !canSetPrevious())
const nextDisabled = computed(() => !canSetNext())

const previousDate = () => {
  if (previousDisabled.value) {
    return
  }

  setPrevious()

  marketingEventService.publish('recent_imagery_past_snapshots_viewed', {
    current_index: getIndex(),
  })
}

const nextDate = () => {
  if (nextDisabled.value) {
    return
  }

  setNext()

  marketingEventService.publish('recent_imagery_past_snapshots_viewed', {
    current_index: getIndex(),
  })
}

const isLoading = computed(getIsLoading)

const showDatePicker = ref(false)
const toggleDatePicker = () => {
  showDatePicker.value = !showDatePicker.value
}
const allDateRanges = computed(() => getAllImageryDateRanges())
const selectedRange = computed(() => {
  const idx = getIndex()
  const arr = allDateRanges.value
  return arr[idx] || null
})

function onDateRangeSelect(range) {
  const idx = allDateRanges.value.findIndex(
    (r) => r.start === range.start && r.end === range.end
  )
  if (idx !== -1) {
    setIndex(idx)
  }
}

/**
 * Computes the display date range.
 *
 * Dates are displayed using UTC.  Server values likely won't have times and run
 * the risk of being rendered one day off, pending local timezone.  Using UTC
 * guarantees consistent historical dates in all cases.
 *
 * Date format examples:
 *   Standard:     Mar 1 - 15, 2023
 *   End of Month: Mar 28 - Apr 11, 2023
 *   End of Year:  Dec 28 2022 - Jan 11, 2023
 */
const datesComputed = computed(() => {
  const startDate = getStartDate()
  const endDate = getEndDate()

  if (!startDate || !endDate) {
    return 'Past Month'
  }

  let startDateFmt = 'UTC:mmm d'
  let endDateFmt = 'UTC:mmm d, yyyy'

  const differentMonths = startDate.getUTCMonth() !== endDate.getUTCMonth()
  const differentYears = startDate.getUTCFullYear() !== endDate.getUTCFullYear()

  if (differentYears) {
    startDateFmt += ', yyyy'
  } else if (!differentMonths && !differentYears) {
    endDateFmt = 'UTC:d, yyyy'
  }

  const startDateStr = dateFormat(startDate, startDateFmt)
  const endDateStr = dateFormat(endDate, endDateFmt)
  return `${startDateStr} - ${endDateStr}`
})

onMounted(() => {
  if (getImageryInfoFetched()) {
    return
  }

  fetchRecentImageryInfo()
})
</script>

<style lang="scss" scoped>
@use '@/styles/system/_shadow.scss';

.all-dates-btn-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.all-dates-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--ys-color-background-primary);
  border-radius: var(--ys-radius-100) var(--ys-radius-100) 0 0;
  padding: var(--ys-spacing-100) var(--ys-spacing-200);
  cursor: pointer;
  width: auto;
  color: var(--ys-color-text-primary);
  margin: 0;
  width: 100%;
}

.icon-list {
  margin-right: 4px;
}

.recent-imagery-control {
  background-color: var(--ys-color-background-primary);
  border-radius: 0 0 var(--ys-radius-100) var(--ys-radius-100);
  box-shadow: 0 -2px 6px 0 rgba(0, 0, 0, 0.14);
  position: relative;
  width: 269px;
  padding: var(--ys-spacing-150);
  height: 44px;
  margin-top: 0;

  .grid-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: 20px;

    .icon-hover {
      &:hover {
        cursor: pointer;
      }
    }

    .icon-hover.icon-disabled {
      cursor: not-allowed;
    }

    .grid-text-center {
      text-align: center;
    }
  }

  .x-loading-spinner {
    margin: 0px auto;
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
